import React, { useState } from 'react';
import { Button, Field, Input, Modal, Note } from '../../../components/app2';

/**
 * Alta de un Super Administrador (rediseño jul-2026).
 *
 * Mismos tres campos y las mismas validaciones; el molde pasa al modal del
 * sistema y la lista de permisos —que ya explicaba bien lo que pasa al guardar—
 * se conserva como nota informativa.
 *
 * El formulario vive en el cuerpo del modal y el botón de guardar lo envía por
 * `form=`, así que el Enter dentro de un campo sigue creando el usuario.
 */

const FORM_ID = 'create-super-admin-form';

const CreateUserModal = ({ isOpen, onClose, onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.firstName?.trim()) {
      newErrors.firstName = 'El nombre es requerido';
    }

    if (!formData?.lastName?.trim()) {
      newErrors.lastName = 'El apellido es requerido';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Formato de email inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);

      // Reset form on success (parent will close modal)
      setFormData({ firstName: '', lastName: '', email: '' });
      setErrors({});
    } catch (error) {
      console.error('Error in form submission:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      open
      onClose={isLoading ? undefined : onClose}
      title="Agregar Super Administrador"
      subtitle="Acceso total al sistema"
      maxWidth={560}
      footer={(
        <>
          <Button variant="outline" size="sm" type="button" onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button size="sm" type="submit" form={FORM_ID} icon="person_add" loading={isLoading}>
            Crear Super Admin
          </Button>
        </>
      )}
    >
      <form id={FORM_ID} onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 14 }}>
          <Field label="Nombre *" htmlFor="new-admin-first-name" error={errors?.firstName}>
            <Input
              id="new-admin-first-name"
              value={formData?.firstName}
              onChange={(e) => handleInputChange('firstName', e?.target?.value)}
              placeholder="Nombre"
              disabled={isLoading}
              required
            />
          </Field>

          <Field label="Apellido *" htmlFor="new-admin-last-name" error={errors?.lastName}>
            <Input
              id="new-admin-last-name"
              value={formData?.lastName}
              onChange={(e) => handleInputChange('lastName', e?.target?.value)}
              placeholder="Apellido"
              disabled={isLoading}
              required
            />
          </Field>
        </div>

        <Field
          label="Correo electrónico *"
          htmlFor="new-admin-email"
          error={errors?.email}
          hint={errors?.email ? undefined : 'Ahí llegará la contraseña temporal.'}
          style={{ marginTop: 14 }}
        >
          <Input
            id="new-admin-email"
            type="email"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            placeholder="admin@ejemplo.com"
            disabled={isLoading}
            required
          />
        </Field>

        <Note tone="info" icon="shield_person" style={{ marginTop: 18 }}>
          <strong>Qué pasa al guardar:</strong> se crea la cuenta con acceso completo a la
          plataforma (empresas, usuarios, facturación y configuración), se genera una contraseña
          automática y se envía por correo. En su primer inicio de sesión tendrá que cambiarla.
        </Note>
      </form>
    </Modal>
  );
};

export default CreateUserModal;
